import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../services/api.js'
import { useAuth } from '../../hooks/useAuth.js'
import ContractDocumentViewer from './ContractDocumentViewer.jsx'
import AIContractRisk from '../ai/AIContractRisk.jsx'
import SignaturePad from '../common/SignaturePad.jsx'
import { formatDate, formatDateTime } from '../../utils/helpers'

const STATUS_STYLES = {
  Draft: 'bg-gray-100 text-gray-700',
  'Pending Review': 'bg-amber-100 text-amber-700',
  'Pending Signature': 'bg-blue-100 text-blue-700',
  Active: 'bg-green-100 text-green-700',
  Terminated: 'bg-red-100 text-red-700',
  Expired: 'bg-gray-100 text-gray-500',
}

function InfoRow({ label, value, private_ }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-800" data-private={private_ || undefined}>{value || '—'}</span>
    </div>
  )
}

export default function ContractDetail() {
  const { id } = useParams()
  const { t } = useTranslation('contracts')
  const navigate = useNavigate()
  const { user } = useAuth()

  const [contract, setContract] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  // Signature-flow state (Phase F — typed-name + checkbox; v2.6 — optional drawn signature)
  const [agreed, setAgreed] = useState(false)
  const [sigData, setSigData] = useState(null)   // base64 PNG from the draw-signature canvas
  const [sigName, setSigName] = useState('')
  // Consultant must verify their bank details are correct before signing (they go on the
  // contract + every invoice). `myBanking` = their current banking (null until loaded / none).
  const [myBanking, setMyBanking] = useState(null)
  const [bankConfirmed, setBankConfirmed] = useState(false)
  // Read-only banking view for PM/Admin (and the consultant, outside the sign-gate flow) —
  // payment goes to this account, so whoever manages/approves the engagement should see it,
  // not just the consultant editing it blind.
  const [contractBanking, setContractBanking] = useState(null)

  const isAdmin = user?.role === 'Admin'
  const isConsultant = user?.role === 'Consultant'
  const isPM = user?.role === 'Project Manager'

  const loadContract = () => {
    setLoading(true)
    api.get(`/contracts/${id}`)
      .then(r => setContract(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load contract'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadContract() }, [id])
  // Load the consultant's own banking so we can show it + gate signing on confirming it.
  useEffect(() => {
    if (isConsultant) api.get('/users/me/banking').then(r => setMyBanking(r.data)).catch(() => {})
  }, [isConsultant, id])
  // Load this contract's banking for the read-only display (PM/Admin/Finance/consultant).
  useEffect(() => {
    api.get(`/contracts/${id}/banking-details`).then(r => setContractBanking(r.data)).catch(() => {})
  }, [id])

  const doAction = async (path, method = 'post', payload = null) => {
    setActionLoading(true)
    setActionError(null)
    setSuccessMsg(null)
    try {
      const resp = method === 'post'
        ? await api.post(path, payload)
        : await api.put(path, payload)
      setSuccessMsg(resp.data?.detail || 'Action completed')
      loadContract()
    } catch (e) {
      setActionError(e.response?.data?.detail || 'Action failed')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      </div>
    )
  }

  if (!contract) return null

  const canConsultantSign =
    isConsultant &&
    contract.consultant_id === user?.id &&
    contract.status === 'Pending Review'

  // v3.5: an Admin OR a delegated scoped approver may counter-sign (backend sets can_counter_sign).
  const canAdminSign = (contract.can_counter_sign ?? isAdmin) && contract.status === 'Pending Signature'

  // Banking is editable by the owning consultant at ANY contract stage — they can keep
  // their payment account current (it feeds the contract doc and every invoice). Only a
  // Terminated/Expired contract is closed to edits.
  const showBankingButton =
    isConsultant &&
    contract.consultant_id === user?.id &&
    !['Terminated', 'Expired'].includes(contract.status)

  const docUrl = contract.generated_doc_url

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/contracts')}
            className="text-xs text-gray-400 hover:text-gray-600 mb-2 flex items-center gap-1"
          >
            ← Back to Contracts
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {contract.contract_number || `Contract #${contract.id}`}
          </h1>
          <span className={`mt-1 inline-block px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[contract.status] || 'bg-gray-100 text-gray-600'}`}>
            {contract.status}
          </span>
        </div>

        {docUrl && (
          <DownloadButton contractId={contract.id} s3Key={docUrl} />
        )}
      </div>

      {/* Feedback banners */}
      {actionError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{actionError}</div>
      )}
      {successMsg && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg">{successMsg}</div>
      )}

      {/* Contract details */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
        <h3 className="font-semibold text-gray-700 mb-3">{t('detail.title')}</h3>
        <InfoRow label="Contract Number" value={contract.contract_number} />
        <InfoRow label="Project ID" value={`#${contract.project_id}`} />
        <InfoRow
          label="Total Fee"
          value={
            contract.budget_type === 'Actual'
              ? 'Reimbursable — billed at actual cost (claim with supporting bills)'
              : contract.budget_type === 'Hybrid'
                ? `${contract.total_fee ? `$${Number(contract.total_fee).toLocaleString()}` : 'Fixed fee'} + actual reimbursable expenses`
                : (contract.total_fee ? `$${Number(contract.total_fee).toLocaleString()}` : null)
          }
        />
        <InfoRow
          label="Effective Date"
          value={contract.effective_date ? formatDate(contract.effective_date) : null}
        />
        <InfoRow
          label="End Date"
          value={contract.end_date ? formatDate(contract.end_date) : null}
        />
        <InfoRow
          label="Signed At"
          value={contract.signed_at ? formatDateTime(contract.signed_at) : null}
        />
        {contract.ninja_project_id && (
          <InfoRow label="Invoice Ninja Project" value={contract.ninja_project_id} />
        )}
      </div>

      {/* Banking details section — payment goes to this account, so the PM managing the
          engagement and Admin/Finance processing payment can see it here too, not just the
          consultant who owns it. Banking is still edited in ONE place (Profile Settings); the
          consultant's edit button sends them there and back. */}
      {(showBankingButton || isAdmin || isPM) && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-700">{t('detail2.payment_banking')}</h3>
            {showBankingButton && (
              <button
                onClick={() => navigate(`/profile-settings?edit=banking&return=${encodeURIComponent(`/contracts/${contract.id}`)}`)}
                className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-100"
              >
                {contract.status === 'Draft' ? 'Add Banking Details' : 'Update Banking Details'}
              </button>
            )}
          </div>
          {contract.status === 'Draft' && showBankingButton && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              Your contract is in Draft status because banking details are missing. Add your banking details in your profile to proceed.
            </p>
          )}
          {contractBanking?.has_banking ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <InfoRow label="Account Holder" value={contractBanking.account_holder_name} private_ />
              <InfoRow label="Bank Name" value={contractBanking.bank_name} />
              <InfoRow label="Account Number" value={contractBanking.account_number} private_ />
              <InfoRow label="IBAN" value={contractBanking.iban} private_ />
              <InfoRow label="SWIFT / BIC" value={contractBanking.swift_code} private_ />
              <InfoRow label="Currency" value={contractBanking.currency} />
              <InfoRow label="Country" value={contractBanking.country} />
              <InfoRow label="Bank Address" value={contractBanking.bank_address} private_ />
            </div>
          ) : (
            !showBankingButton && contract.status !== 'Draft' && (
              <p className="text-sm text-gray-400">{t('detail2.no_bank_on_file')}</p>
            )
          )}
        </div>
      )}

      {/* ── Full Contract Document Viewer (Phase F) ──
          Shown to consultant + admin + project's PM, once banking is on file.
          Lets all parties read the FULL merged contract before signing. */}
      {contract.status !== 'Draft' && (
        <div className="mb-5">
          <ContractDocumentViewer
            contractId={contract.id}
            contractStatus={contract.status}
            userRole={user?.role}
          />
        </div>
      )}

      {/* Signature workflow + actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
        <h3 className="font-semibold text-gray-700 mb-4">{t('detail2.signature_workflow')}</h3>

        <div className="space-y-3 mb-5">
          <SignatureStep label="1. Contract Generated" done={!!contract.generated_doc_url || contract.status !== 'Draft'} />
          <SignatureStep
            label="2. Consultant Review & Sign"
            done={['Pending Signature', 'Active', 'Terminated', 'Expired'].includes(contract.status)}
            active={canConsultantSign}
          />
          <SignatureStep
            label="3. Company Counter-Signature"
            done={['Active', 'Terminated', 'Expired'].includes(contract.status)}
            active={canAdminSign}
          />
          <SignatureStep label="4. Contract Active" done={contract.status === 'Active'} />
        </div>

        {/* AI risk scan — second pair of eyes before anyone signs */}
        {(canConsultantSign || canAdminSign) && (
          <div className="mt-4">
            <AIContractRisk contractId={contract.id} />
          </div>
        )}

        {/* Draft contract, owning consultant: it can't be signed until banking is on file
            (a Draft becomes 'Pending Review' once banking is saved). Guide them there so the
            contract doesn't dead-end in Draft — which would also stall the Admin counter-sign. */}
        {isConsultant && contract.consultant_id === user?.id && contract.status === 'Draft' && (
          <div className="mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
            📋 This contract is ready, but you must <strong>add your bank details</strong> before you can sign it
            (they appear on the contract &amp; every invoice). Use the <strong>{t('detail2.bank_details')}</strong> button above —
            once saved, this contract unlocks for signing.
          </div>
        )}

        {/* Bank-details verification gate — the consultant must confirm their bank details
            are correct before signing, since those exact details go on this contract and every
            invoice. Shows their current details + a required checkbox that unlocks signing. */}
        {canConsultantSign && (
          <div className="mt-4 p-4 rounded-xl border border-amber-300 bg-amber-50">
            <p className="text-sm font-semibold text-amber-900">⚠️ Verify your bank details before signing</p>
            <p className="text-xs text-amber-800 mt-1">
              These are the exact details that go on your contract and every invoice — payment is sent to this account.
            </p>
            <div className="mt-2 text-xs text-amber-900 bg-white/70 border border-amber-200 rounded-lg p-2.5 space-y-0.5">
              {myBanking?.has_banking ? (
                <>
                  {myBanking.account_holder_name && <div><span className="font-medium">A/C name:</span> <span data-private>{myBanking.account_holder_name}</span></div>}
                  {myBanking.bank_name && <div><span className="font-medium">{t('detail2.bank_colon')}</span> {myBanking.bank_name}</div>}
                  {myBanking.account_number && <div><span className="font-medium">A/C no:</span> <span data-private>{myBanking.account_number}</span></div>}
                  {myBanking.swift_code && <div><span className="font-medium">{t('detail2.swift_colon')}</span> <span data-private>{myBanking.swift_code}</span></div>}
                  {myBanking.currency && <div><span className="font-medium">{t('detail2.currency_colon')}</span> {myBanking.currency}</div>}
                </>
              ) : (
                <div className="text-red-600 font-medium">{t('detail2.no_bank_before_sign')}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(`/profile-settings?edit=banking&return=${encodeURIComponent(`/contracts/${contract.id}`)}`)}
              className="mt-2 text-xs font-medium text-blue-600 hover:underline"
            >
              {myBanking?.has_banking ? 'Update bank details' : 'Add bank details'}
            </button>
            <label className={`flex items-start gap-2 mt-3 text-xs ${myBanking?.has_banking ? 'text-amber-900 cursor-pointer' : 'text-gray-400'}`}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={bankConfirmed}
                disabled={!myBanking?.has_banking}
                onChange={e => setBankConfirmed(e.target.checked)}
              />
              <span>I have verified the bank details above are correct.</span>
            </label>
          </div>
        )}

        {/* Consultant signing panel — typed name + checkbox required */}
        {canConsultantSign && (
          <SigningPanel
            who="consultant"
            actionLabel="Sign Contract"
            agreed={agreed}
            setAgreed={setAgreed}
            sigName={sigName}
            setSigName={setSigName}
            sigData={sigData}
            setSigData={setSigData}
            loading={actionLoading}
            contractId={contract.id}
            extraGateMet={!!(myBanking?.has_banking && bankConfirmed)}
            onUploaded={() => window.location.reload()}
            onSign={() => {
              if (!myBanking?.has_banking) { setActionError('Please add your bank details before signing.'); return }
              if (!bankConfirmed) { setActionError('Please confirm your bank details are correct before signing.'); return }
              if (!agreed) { setActionError('Please tick the agreement checkbox.'); return }
              if (!sigName.trim()) { setActionError('Please type your full name to sign.'); return }
              // Keep the typed name + ticked box visible through submit — the panel is
              // replaced by the signed state on reload; clearing them mid-request made it
              // look like nothing was signed (admin confusion).
              doAction(`/contracts/${contract.id}/sign/consultant`, 'post', { signature_name: sigName.trim(), agreed: true, signature_data: sigData })
            }}
          />
        )}

        {/* Admin counter-signing panel */}
        {canAdminSign && (
          <SigningPanel
            who="admin"
            actionLabel="Counter-Sign (Make Active)"
            agreed={agreed}
            setAgreed={setAgreed}
            sigName={sigName}
            setSigName={setSigName}
            sigData={sigData}
            setSigData={setSigData}
            loading={actionLoading}
            onSign={() => {
              if (!agreed) { setActionError('Please tick the agreement checkbox.'); return }
              if (!sigName.trim()) { setActionError('Please type your full name to counter-sign.'); return }
              doAction(`/contracts/${contract.id}/sign/company`, 'post', { signature_name: sigName.trim(), agreed: true, signature_data: sigData })
            }}
          />
        )}
      </div>

      {/* Admin: generate contract manually if Draft */}
      {isAdmin && contract.status === 'Draft' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-700 mb-2">{t('detail2.admin_actions')}</h3>
          <button
            onClick={() => doAction(`/contracts/generate/${contract.project_id}`)}
            disabled={actionLoading}
            className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
          >
            Re-generate Contract Document
          </button>
        </div>
      )}

      {/* Termination / Extension — maker-checker (PM → Admin → consultant confirm) */}
      {['Active', 'Terminated', 'Expired'].includes(contract.status) && (
        <ContractModifications contractId={contract.id} contract={contract} user={user} onChanged={loadContract} />
      )}

      {/* Contract History — earlier versions superseded when scope changed (hidden until opened) */}
      <ContractHistory contractId={contract.id} />
    </div>
  )
}

function ContractHistory({ contractId }) {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('contracts')
  const [open, setOpen] = useState(false)
  const [data, setData] = useState(null)
  const [loaded, setLoaded] = useState(false)

  const load = () => {
    api.get(`/contracts/${contractId}/versions`)
      .then(r => setData(r.data))
      .catch(() => setData({ versions: [] }))
      .finally(() => setLoaded(true))
  }
  const toggle = () => { const next = !open; setOpen(next); if (next && !loaded) load() }

  const download = async (versionId, versionNo) => {
    try {
      const r = await api.get(`/contracts/${contractId}/versions/${versionId}/download`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = `contract-v${versionNo}`
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch { /* ignore */ }
  }

  const versions = data?.versions || []
  return (
    <div className="mt-4 bg-white border border-gray-200 rounded-xl">
      <button onClick={toggle} className="w-full flex items-center justify-between px-5 py-3 text-left">
        <span className="text-sm font-semibold text-gray-800">📜 Contract History</span>
        <span className="text-xs text-gray-500">
          {data ? `Current v${data.current_version}${versions.length ? ` · ${versions.length} earlier version${versions.length > 1 ? 's' : ''}` : ''}` : ''} {open ? '▲' : '▼'}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-gray-100">
          {!loaded ? (
            <p className="text-xs text-gray-400 py-3">{t('detail2.loading')}</p>
          ) : versions.length === 0 ? (
            <p className="text-xs text-gray-500 py-3">No earlier versions — this contract hasn't been re-issued yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {versions.map(v => (
                <li key={v.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Version {v.version_number}
                      <span className="ml-2 text-[10px] font-semibold uppercase bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">{t('detail2.superseded')}</span>
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {v.status ? `${v.status} · ` : ''}Fee ${Number(v.total_fee || 0).toLocaleString()}
                      {v.superseded_at ? ` · replaced ${new Date(v.superseded_at).toLocaleDateString()}` : ''}
                    </p>
                    {v.superseded_reason && <p className="text-[11px] text-gray-400 mt-0.5">{v.superseded_reason}</p>}
                    {v.consultant_signed_name && <p className="text-[11px] text-gray-400 mt-0.5">Signed by {v.consultant_signed_name}</p>}
                  </div>
                  {v.has_document && (
                    <button onClick={() => download(v.id, v.version_number)}
                      className="shrink-0 text-xs text-blue-600 hover:text-blue-700 hover:underline">{tc('actions.download')}</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function ContractModifications({ contractId, contract, user, onChanged }) {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('contracts')
  const [mods, setMods] = useState([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [form, setForm] = useState(null) // 'terminate' | 'extend' | null
  const [vals, setVals] = useState({})

  const isAdmin = user?.role === 'Admin'
  const isPM = user?.role === 'Project Manager'
  const isOwnerConsultant = user?.role === 'Consultant' && contract.consultant_id === user?.id

  const load = () => api.get(`/contracts/${contractId}/modifications`).then(r => setMods(r.data || [])).catch(() => {})
  useEffect(() => { load() }, [contractId])

  const call = async (path, payload) => {
    setBusy(true); setErr(null)
    try {
      await api.post(path, payload || {})
      setForm(null); setVals({})
      await load(); onChanged && onChanged()
    } catch (e) {
      setErr(e.response?.data?.detail || 'Action failed')
    } finally { setBusy(false) }
  }

  const pendingTerm = mods.find(m => m.type === 'Termination' && m.status === 'Proposed')
  const approvedTerm = mods.find(m => m.type === 'Termination' && m.status === 'Approved')
  const pendingExt = mods.find(m => m.type === 'Extension' && m.status === 'Proposed')
  const terminated = contract.status === 'Terminated'

  const badge = (s) => ({
    Proposed: 'bg-amber-100 text-amber-700', Approved: 'bg-blue-100 text-blue-700',
    Confirmed: 'bg-green-100 text-green-700',
  }[s] || 'bg-gray-100 text-gray-600')

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-5">
      <h3 className="font-semibold text-gray-700 mb-1">{t('detail2.contract_modifications')}</h3>
      <p className="text-xs text-gray-500 mb-4">Early termination and extensions follow a maker–checker flow (PM initiates → Admin approves{terminated ? '' : ' → consultant confirms termination'}).</p>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm mb-3">{err}</div>}

      {/* Existing modifications */}
      {mods.length > 0 && (
        <div className="space-y-2 mb-4">
          {mods.map(m => (
            <div key={m.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800">{m.type}</p>
                <p className="text-xs text-gray-500 truncate">{m.description}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${badge(m.status)}`}>{m.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {!terminated && (
        <div className="flex flex-wrap gap-2">
          {(isPM || isAdmin) && !pendingTerm && !approvedTerm && (
            <button onClick={() => { setForm('terminate'); setVals({}) }} className="text-xs px-3 py-1.5 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100">{t('detail2.request_termination')}</button>
          )}
          {(isPM || isAdmin) && !pendingExt && (
            <button onClick={() => { setForm('extend'); setVals({}) }} className="text-xs px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">{t('detail2.request_extension')}</button>
          )}
          {isAdmin && pendingTerm && (
            <button disabled={busy} onClick={() => call(`/contracts/${contractId}/terminate/approve`)} className="text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t('detail2.approve_termination')}</button>
          )}
          {isAdmin && pendingExt && (
            <button disabled={busy} onClick={() => call(`/contracts/${contractId}/extend/approve`)} className="text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t('detail2.approve_extension')}</button>
          )}
          {(isOwnerConsultant || isAdmin) && approvedTerm && (
            <button disabled={busy} onClick={() => call(`/contracts/${contractId}/terminate/confirm`)} className="text-xs px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">{t('detail2.confirm_termination')}</button>
          )}
        </div>
      )}

      {/* Termination form */}
      {form === 'terminate' && (
        <div className="mt-4 border-t border-gray-100 pt-4 space-y-2">
          <p className="text-sm font-semibold text-gray-700">{t('detail2.request_early_termination')}</p>
          <textarea placeholder={t('detail2.ph_termination_reason')} value={vals.reason || ''} onChange={e => setVals({ ...vals, reason: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" rows={2} />
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={vals.termination_date || ''} onChange={e => setVals({ ...vals, termination_date: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
            <input type="number" placeholder={t('detail2.ph_final_settlement')} value={vals.final_settlement_amount || ''} onChange={e => setVals({ ...vals, final_settlement_amount: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="text-xs px-3 py-1.5 rounded-md bg-gray-100 text-gray-700">{tc('actions.cancel')}</button>
            <button disabled={busy || !(vals.reason || '').trim()} onClick={() => call(`/contracts/${contractId}/terminate/request`, { reason: vals.reason, termination_date: vals.termination_date || null, final_settlement_amount: vals.final_settlement_amount ? parseFloat(vals.final_settlement_amount) : null })} className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">{t('detail2.submit_request')}</button>
          </div>
        </div>
      )}

      {/* Extension form */}
      {form === 'extend' && (
        <div className="mt-4 border-t border-gray-100 pt-4 space-y-2">
          <p className="text-sm font-semibold text-gray-700">{t('detail2.request_extension_long')}</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">{t('detail2.new_end_date_req')}</label>
              <input type="date" value={vals.new_end_date || ''} onChange={e => setVals({ ...vals, new_end_date: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">{t('detail2.revised_total_fee')}</label>
              <input type="number" placeholder={t('detail2.ph_optional')} value={vals.revised_fee || ''} onChange={e => setVals({ ...vals, revised_fee: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <textarea placeholder={t('detail2.ph_added_scope')} value={vals.added_scope || ''} onChange={e => setVals({ ...vals, added_scope: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" rows={2} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="text-xs px-3 py-1.5 rounded-md bg-gray-100 text-gray-700">{tc('actions.cancel')}</button>
            <button disabled={busy || !(vals.new_end_date || '')} onClick={() => call(`/contracts/${contractId}/extend/request`, { new_end_date: vals.new_end_date, added_scope: vals.added_scope || null, revised_fee: vals.revised_fee ? parseFloat(vals.revised_fee) : null })} className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">{t('detail2.submit_request')}</button>
          </div>
        </div>
      )}
    </div>
  )
}

function SignatureStep({ label, done, active }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
        ${done ? 'bg-green-100 text-green-600' : active ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-200 animate-pulse' : 'bg-gray-100 text-gray-400'}`}>
        {done ? '✓' : '○'}
      </div>
      <span className={`text-sm flex-1 ${done ? 'text-gray-800 font-medium' : active ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>{label}</span>
      {active && <span className="text-xs text-blue-600 font-medium">← Action required</span>}
    </div>
  )
}

function SigningPanel({ who, actionLabel, agreed, setAgreed, sigName, setSigName, sigData, setSigData, loading, onSign, contractId, onUploaded, extraGateMet = true }) {
  const { t } = useTranslation('contracts')
  const isAdmin = who === 'admin'
  const title = isAdmin ? 'Counter-Sign the Contract' : 'Sign the Contract'
  const partyLabel = isAdmin ? 'company representative' : 'consultant'
  // extraGateMet lets the caller add a precondition (e.g. consultant must confirm bank details).
  const disabled = loading || !agreed || !sigName.trim() || !extraGateMet

  // Upload-signed-copy tab (consultant only)
  const [signMode, setSignMode] = useState('type') // 'type' | 'upload'
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadDone, setUploadDone] = useState(false)

  const handleUploadSigned = async () => {
    if (!uploadFile || !agreed) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', uploadFile)
      fd.append('signature_name', sigName.trim() || who)
      fd.append('agreed', 'true')
      // Multipart upload endpoint — stores the signed copy + moves to Pending Signature.
      const api = (await import('../../services/api')).default
      await api.post(`/contracts/${contractId}/sign/consultant-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setUploadDone(true)
      if (onUploaded) onUploaded()
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={`mt-4 p-4 rounded-xl border-2 ${isAdmin ? 'border-purple-200 bg-purple-50' : 'border-blue-200 bg-blue-50'}`}>
      <h4 className="text-sm font-semibold text-gray-800 mb-1">{title}</h4>
      <p className="text-xs text-gray-500 mb-3">
        Review the full contract document above. By signing you confirm you have read and agree to all terms as the {partyLabel}.
      </p>

      {/* Sign mode tabs — only for consultant */}
      {!isAdmin && (
        <div className="flex gap-1 mb-4 bg-white rounded-lg p-0.5 border border-gray-200 w-fit">
          <button
            onClick={() => setSignMode('type')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${signMode === 'type' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            ✍️ E-Sign (Type Name)
          </button>
          <button
            onClick={() => setSignMode('upload')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${signMode === 'upload' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            📎 Upload Signed Copy
          </button>
        </div>
      )}

      {/* Agreement checkbox — always */}
      <label className="flex items-start gap-2 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
        <span className="text-sm text-gray-700">
          I have read and agree to the terms of this Consultancy Agreement.
        </span>
      </label>

      {/* E-Sign tab — typed name (required) + optional draw/upload signature image */}
      {(isAdmin || signMode === 'type') && (
        <>
          <SignaturePad
            name={sigName}
            onNameChange={setSigName}
            signatureDataUrl={sigData}
            onSignatureChange={setSigData}
            namePlaceholder={isAdmin ? 'e.g. Bharat Admin' : 'e.g. Abhi Consultant'}
          />

          <button
            onClick={onSign}
            disabled={disabled}
            className={`w-full sm:w-auto px-5 py-2 text-sm font-semibold text-white rounded-lg transition
              ${isAdmin ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}
              disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? 'Signing…' : actionLabel}
          </button>
        </>
      )}

      {/* Upload signed copy tab */}
      {!isAdmin && signMode === 'upload' && (
        uploadDone ? (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm font-medium">
            ✅ Signed copy uploaded — contract is now pending counter-signature from Admin.
          </div>
        ) : (
          <>
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-2">
                Download the contract, sign it physically or digitally, then upload the signed copy here.
              </p>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {uploadFile && <p className="text-xs text-gray-500 mt-1">Selected: {uploadFile.name}</p>}
            </div>
            <label className="block mb-3">
              <span className="text-xs font-medium text-gray-600 block mb-1">{t('detail2.your_full_name_req')}</span>
              <input
                type="text"
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder={t('detail2.ph_your_name')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <button
              onClick={handleUploadSigned}
              disabled={uploading || !uploadFile || !agreed || !sigName.trim()}
              className="w-full sm:w-auto px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading…' : '📎 Upload Signed Contract'}
            </button>
          </>
        )
      )}
    </div>
  )
}

function DownloadButton({ contractId, s3Key }) {
  const [loading, setLoading] = useState(false)

  const download = async () => {
    setLoading(true)
    try {
      const resp = await api.get(`/contracts/${contractId}/download`)
      if (resp.data?.url) {
        window.open(resp.data.url, '_blank')
      }
    } catch {
      // If endpoint doesn't exist yet, do nothing silently
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      onClick={download}
      disabled={loading}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-200 disabled:opacity-50"
    >
      {loading ? '...' : '⬇ Download Contract'}
    </button>
  )
}
